import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, useLanguage } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  rectSortingStrategy, useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Image as ImageIcon, Upload, X, GripVertical, Plus, Loader2,
  Eye, RefreshCw, Smartphone, Monitor, Sparkles, Check,
  Instagram, Facebook, Globe, Music2, Palette, Save,
  ChevronRight, AlertCircle, ArrowLeft
} from "lucide-react";

// =============== Image compression util ===============
const compressImage = (file, maxW = 1600, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxW / img.width, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error("Compression failed"));
          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
          resolve(compressed);
        }, "image/jpeg", quality);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// =============== Sortable Gallery Item ===============
const SortableImage = ({ image, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 group" data-testid={`gallery-item-${image.id}`}>
      <img src={image.url} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-start justify-between p-2 opacity-0 group-hover:opacity-100">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1.5 rounded-lg bg-white/90 hover:bg-white" title="Sposta">
          <GripVertical className="w-4 h-4 text-slate-700" />
        </button>
        <button onClick={() => onRemove(image.id)} className="p-1.5 rounded-lg bg-red-500 hover:bg-red-600" title="Rimuovi" data-testid={`remove-image-${image.id}`}>
          <X className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
};

// =============== Image dropzone ===============
const ImageDropzone = ({ onFiles, accept = "image/*", multiple = false, label, hint, testId }) => {
  const inputRef = useRef();
  const [hovering, setHovering] = useState(false);

  const handle = (files) => {
    if (!files || files.length === 0) return;
    onFiles(multiple ? Array.from(files) : [files[0]]);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
      onDragLeave={() => setHovering(false)}
      onDrop={(e) => { e.preventDefault(); setHovering(false); handle(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all ${hovering ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30" : "border-slate-300 dark:border-slate-700 hover:border-violet-400 hover:bg-slate-50 dark:hover:bg-slate-800/50"}`}
      data-testid={testId}
    >
      <Upload className="w-7 h-7 mx-auto mb-2 text-slate-400" />
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => handle(e.target.files)}
      />
    </div>
  );
};

// =============== Section Card wrapper ===============
const Section = ({ title, description, icon: Icon, children, testId }) => (
  <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 sm:p-6" data-testid={testId}>
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/40 dark:to-purple-900/40 flex items-center justify-center flex-shrink-0">
        {Icon && <Icon className="w-4.5 h-4.5 text-violet-600 dark:text-violet-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-base">{title}</h3>
        {description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
      </div>
    </div>
    {children}
  </section>
);

// =============== Toggle row ===============
const ToggleRow = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-start justify-between py-3 gap-4">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium">{label}</p>
      {description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

// =============== Main Editor ===============
const SmartPageEditor = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [previewMode, setPreviewMode] = useState("desktop"); // mobile | desktop
  const [previewKey, setPreviewKey] = useState(0);
  const [showPreview, setShowPreview] = useState(true);

  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(true); // skip first auto-save on initial load

  // Debounced save effect — runs whenever tenant changes (after initial load)
  useEffect(() => {
    if (!tenant) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => doSave(), 700);
    return () => saveTimerRef.current && clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant?.smartpage_settings,
    tenant?.about_short,
    tenant?.about_long,
    tenant?.gallery,
    tenant?.social_links,
    tenant?.branding,
    tenant?.logo_url,
    tenant?.cover_image,
  ]);

  const fetchTenant = async () => {
    try {
      const r = await axios.get(`${API}/tenants/me`, { withCredentials: true });
      const t = r.data || {};
      // Ensure default smartpage_settings
      if (!t.smartpage_settings) {
        t.smartpage_settings = {
          show_prices: true, show_team: true, show_gallery: true,
          show_reviews: true, cta_whatsapp_enabled: true, footer_link_enabled: true,
        };
      }
      if (!t.social_links) t.social_links = {};
      if (!t.branding) t.branding = { primary_color: "#6366f1", secondary_color: "#8b5cf6" };
      if (!Array.isArray(t.gallery)) t.gallery = [];
      // Normalize gallery items to {id,url}
      t.gallery = t.gallery.map((g) => typeof g === "string" ? { id: `g_${Math.random().toString(36).slice(2, 9)}`, url: g } : g);
      setTenant(t);
    } catch (e) {
      toast.error(language === "it" ? "Errore caricamento" : "Load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSave = async () => {
    if (!tenant?.tenant_id) return;
    setSaving(true);
    try {
      const payload = {
        smartpage_settings: tenant.smartpage_settings,
        about_short: tenant.about_short || "",
        about_long: tenant.about_long || "",
        gallery: tenant.gallery,
        social_links: tenant.social_links,
        branding: tenant.branding,
        logo_url: tenant.logo_url || "",
        cover_image: tenant.cover_image || "",
      };
      await axios.patch(`${API}/tenants/${tenant.tenant_id}`, payload, { withCredentials: true });
      setSavedAt(new Date());
      // Refresh preview
      setPreviewKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === "it" ? "Errore salvataggio" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  // ---- Update helpers ----
  const set = (path, value) => {
    setTenant((prev) => {
      const clone = { ...prev };
      const keys = path.split(".");
      let cur = clone;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  // ---- Image upload handlers ----
  const uploadImage = async (file, endpoint) => {
    const compressed = await compressImage(file, endpoint === "logo" ? 800 : 1600, 0.85);
    const fd = new FormData();
    fd.append("file", compressed);
    const r = await axios.post(`${API}/upload/${endpoint}`, fd, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return r.data.url;
  };

  const handleLogoUpload = async (files) => {
    try {
      const url = await uploadImage(files[0], "logo");
      set("logo_url", url);
      toast.success(language === "it" ? "Logo caricato!" : "Logo uploaded!");
    } catch (e) {
      toast.error(language === "it" ? "Errore upload logo" : "Logo upload error");
    }
  };

  const handleCoverUpload = async (files) => {
    try {
      const url = await uploadImage(files[0], "cover");
      set("cover_image", url);
      toast.success(language === "it" ? "Copertina caricata!" : "Cover uploaded!");
    } catch (e) {
      toast.error(language === "it" ? "Errore upload copertina" : "Cover upload error");
    }
  };

  const handleGalleryUpload = async (files) => {
    const newItems = [];
    for (const file of files) {
      try {
        const url = await uploadImage(file, "gallery");
        newItems.push({ id: `g_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, url });
      } catch (e) {/* skip */}
    }
    if (newItems.length > 0) {
      setTenant((prev) => ({ ...prev, gallery: [...(prev.gallery || []), ...newItems] }));
      toast.success(language === "it" ? `${newItems.length} immagini caricate` : `${newItems.length} images uploaded`);
    }
  };

  const removeGalleryItem = (id) => {
    setTenant((prev) => ({ ...prev, gallery: prev.gallery.filter((g) => g.id !== id) }));
  };

  // ---- Sensors for DnD ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTenant((prev) => {
      const oldIdx = prev.gallery.findIndex((g) => g.id === active.id);
      const newIdx = prev.gallery.findIndex((g) => g.id === over.id);
      return { ...prev, gallery: arrayMove(prev.gallery, oldIdx, newIdx) };
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      </AdminLayout>
    );
  }

  if (!tenant) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p>{language === "it" ? "Tenant non trovato" : "Tenant not found"}</p>
        </div>
      </AdminLayout>
    );
  }

  const sps = tenant.smartpage_settings || {};
  const social = tenant.social_links || {};
  const branding = tenant.branding || {};
  const previewUrl = `/book/${tenant.slug}`;

  return (
    <AdminLayout>
      <div className="max-w-[1600px] mx-auto" data-testid="smartpage-editor">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-500" />
              {language === "it" ? "Smart Page Editor" : "Smart Page Editor"}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {language === "it"
                ? "Personalizza il tuo mini-sito pubblico — modifiche salvate automaticamente."
                : "Customize your public mini-site — changes save automatically."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Save status pill */}
            <div className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800" data-testid="save-status">
              {saving ? (
                <><Loader2 className="w-3 h-3 animate-spin text-violet-500" /><span>{language === "it" ? "Salvataggio..." : "Saving..."}</span></>
              ) : savedAt ? (
                <><Check className="w-3 h-3 text-emerald-500" /><span className="text-slate-600 dark:text-slate-300">{language === "it" ? "Salvato" : "Saved"} · {savedAt.toLocaleTimeString().slice(0, 5)}</span></>
              ) : (
                <><span className="text-slate-500">{language === "it" ? "Pronto" : "Ready"}</span></>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(previewUrl, "_blank")}
              data-testid="open-public-page"
            >
              <Eye className="w-4 h-4 mr-1" />
              {language === "it" ? "Apri pagina" : "Open page"}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr,560px] xl:grid-cols-[1fr,640px] gap-5">
          {/* ============ EDITOR (left) ============ */}
          <div className="space-y-4">
            {/* Branding */}
            <Section title={language === "it" ? "Brand & Identità" : "Brand & Identity"} description={language === "it" ? "Logo, copertina e colori del tuo brand." : "Logo, cover and brand colors."} icon={Palette} testId="section-branding">
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Logo */}
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-2">{language === "it" ? "Logo" : "Logo"}</label>
                  {tenant.logo_url ? (
                    <div className="relative group rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-4 flex items-center justify-center h-32">
                      <img src={tenant.logo_url} alt="logo" className="max-h-full max-w-full object-contain" data-testid="current-logo" />
                      <button onClick={() => set("logo_url", "")} className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-logo">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <ImageDropzone onFiles={handleLogoUpload} label={language === "it" ? "Trascina o clicca" : "Drop or click"} hint="PNG / JPG · max 5MB" testId="logo-dropzone" />
                  )}
                </div>
                {/* Cover */}
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-2">{language === "it" ? "Copertina" : "Cover image"}</label>
                  {tenant.cover_image ? (
                    <div className="relative group rounded-2xl overflow-hidden h-32 bg-slate-100">
                      <img src={tenant.cover_image} alt="cover" className="w-full h-full object-cover" data-testid="current-cover" />
                      <button onClick={() => set("cover_image", "")} className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-cover">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <ImageDropzone onFiles={handleCoverUpload} label={language === "it" ? "Trascina o clicca" : "Drop or click"} hint="JPG / PNG · 1600×600" testId="cover-dropzone" />
                  )}
                </div>
              </div>

              {/* Brand colors */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1.5">{language === "it" ? "Colore primario" : "Primary color"}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={branding.primary_color || "#6366f1"} onChange={(e) => set("branding.primary_color", e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer" data-testid="primary-color-input" />
                    <Input value={branding.primary_color || "#6366f1"} onChange={(e) => set("branding.primary_color", e.target.value)} className="rounded-xl flex-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1.5">{language === "it" ? "Colore secondario" : "Secondary color"}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={branding.secondary_color || "#8b5cf6"} onChange={(e) => set("branding.secondary_color", e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer" data-testid="secondary-color-input" />
                    <Input value={branding.secondary_color || "#8b5cf6"} onChange={(e) => set("branding.secondary_color", e.target.value)} className="rounded-xl flex-1" />
                  </div>
                </div>
              </div>
            </Section>

            {/* About */}
            <Section title={language === "it" ? "Chi sei" : "About"} description={language === "it" ? "Racconta la tua attività in poche parole e in dettaglio." : "Tell your story briefly and in detail."} icon={Sparkles} testId="section-about">
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1.5">
                    {language === "it" ? "Slogan / breve descrizione" : "Tagline / short description"}
                    <span className="ml-2 text-slate-400">{(tenant.about_short || "").length}/140</span>
                  </label>
                  <Input
                    value={tenant.about_short || ""}
                    onChange={(e) => set("about_short", e.target.value.slice(0, 140))}
                    placeholder={language === "it" ? "Es. Bellezza e relax nel cuore di Milano." : "e.g. Beauty & relax in the heart of the city."}
                    className="rounded-xl"
                    data-testid="about-short-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400 block mb-1.5">{language === "it" ? "Descrizione completa" : "Full description"}</label>
                  <Textarea
                    value={tenant.about_long || ""}
                    onChange={(e) => set("about_long", e.target.value)}
                    placeholder={language === "it" ? "Parla della tua passione, dei tuoi servizi, di cosa ti rende unico..." : "Talk about your passion, your services, what makes you unique..."}
                    rows={5}
                    className="rounded-xl resize-none"
                    data-testid="about-long-input"
                  />
                </div>
              </div>
            </Section>

            {/* Gallery */}
            <Section title={language === "it" ? "Galleria" : "Gallery"} description={language === "it" ? "Trascina per riordinare. Le immagini si comprimono automaticamente." : "Drag to reorder. Images are auto-compressed."} icon={ImageIcon} testId="section-gallery">
              {tenant.gallery.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={tenant.gallery.map((g) => g.id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                      {tenant.gallery.map((img) => (
                        <SortableImage key={img.id} image={img} onRemove={removeGalleryItem} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              <ImageDropzone
                onFiles={handleGalleryUpload}
                multiple
                label={language === "it" ? "Aggiungi foto" : "Add photos"}
                hint={language === "it" ? "Anche più foto insieme · JPG/PNG/WebP" : "Multiple files supported · JPG/PNG/WebP"}
                testId="gallery-dropzone"
              />
            </Section>

            {/* Social */}
            <Section title={language === "it" ? "Social & Web" : "Social & Web"} description={language === "it" ? "Collega i tuoi canali." : "Connect your channels."} icon={Globe} testId="section-social">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-4.5 h-4.5 text-white" />
                  </div>
                  <Input value={social.instagram || ""} onChange={(e) => set("social_links.instagram", e.target.value)} placeholder="https://instagram.com/..." className="rounded-xl" data-testid="social-instagram-input" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-4.5 h-4.5 text-white" />
                  </div>
                  <Input value={social.facebook || ""} onChange={(e) => set("social_links.facebook", e.target.value)} placeholder="https://facebook.com/..." className="rounded-xl" data-testid="social-facebook-input" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-4.5 h-4.5 text-white" />
                  </div>
                  <Input value={social.tiktok || ""} onChange={(e) => set("social_links.tiktok", e.target.value)} placeholder="https://tiktok.com/@..." className="rounded-xl" data-testid="social-tiktok-input" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4.5 h-4.5 text-white" />
                  </div>
                  <Input value={social.website || ""} onChange={(e) => set("social_links.website", e.target.value)} placeholder="https://..." className="rounded-xl" data-testid="social-website-input" />
                </div>
              </div>
            </Section>

            {/* Toggles */}
            <Section title={language === "it" ? "Visualizzazione" : "Visibility"} description={language === "it" ? "Scegli cosa mostrare ai tuoi clienti." : "Choose what to show to your clients."} icon={Eye} testId="section-toggles">
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                <ToggleRow
                  label={language === "it" ? "Mostra prezzi servizi" : "Show service prices"}
                  description={language === "it" ? "Disattiva per nascondere i prezzi e mostrare 'Su richiesta'." : "Disable to hide prices and show 'On request'."}
                  checked={sps.show_prices !== false}
                  onChange={(v) => set("smartpage_settings.show_prices", v)}
                  testId="toggle-show-prices"
                />
                <ToggleRow
                  label={language === "it" ? "Mostra team" : "Show team"}
                  description={language === "it" ? "Sezione professionisti pubblica." : "Public team section."}
                  checked={sps.show_team !== false}
                  onChange={(v) => set("smartpage_settings.show_team", v)}
                  testId="toggle-show-team"
                />
                <ToggleRow
                  label={language === "it" ? "Mostra galleria" : "Show gallery"}
                  description={language === "it" ? "Mostra le foto del locale." : "Display location photos."}
                  checked={sps.show_gallery !== false}
                  onChange={(v) => set("smartpage_settings.show_gallery", v)}
                  testId="toggle-show-gallery"
                />
                <ToggleRow
                  label={language === "it" ? "Mostra recensioni" : "Show reviews"}
                  description={language === "it" ? "Recensioni interne dei clienti." : "Internal customer reviews."}
                  checked={sps.show_reviews !== false}
                  onChange={(v) => set("smartpage_settings.show_reviews", v)}
                  testId="toggle-show-reviews"
                />
                <ToggleRow
                  label={language === "it" ? "Pulsante WhatsApp" : "WhatsApp button"}
                  description={language === "it" ? "CTA WhatsApp nell'hero e nei contatti." : "WhatsApp CTA in hero & contacts."}
                  checked={sps.cta_whatsapp_enabled !== false}
                  onChange={(v) => set("smartpage_settings.cta_whatsapp_enabled", v)}
                  testId="toggle-cta-whatsapp"
                />
                <ToggleRow
                  label={language === "it" ? "Multi-servizio in prenotazione" : "Multi-service booking"}
                  description={language === "it" ? "Permetti al cliente di prenotare più servizi insieme (es. Taglio + Colore)." : "Let clients book multiple services in one go (e.g. Cut + Color)."}
                  checked={sps.allow_multi_service !== false}
                  onChange={(v) => set("smartpage_settings.allow_multi_service", v)}
                  testId="toggle-allow-multi-service"
                />
                <ToggleRow
                  label={language === "it" ? "Footer SmartBooker" : "SmartBooker footer link"}
                  description={language === "it" ? "Link 'Powered by SmartBooker' in fondo alla pagina." : "'Powered by SmartBooker' link at bottom."}
                  checked={sps.footer_link_enabled !== false}
                  onChange={(v) => set("smartpage_settings.footer_link_enabled", v)}
                  testId="toggle-footer-link"
                />
              </div>
            </Section>
          </div>

          {/* ============ PREVIEW (right) ============ */}
          <div className="lg:sticky lg:top-4 lg:self-start hidden lg:block" data-testid="smartpage-preview-pane">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl p-3 mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1 bg-white dark:bg-slate-900 rounded-full p-0.5">
                <button
                  onClick={() => setPreviewMode("desktop")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${previewMode === "desktop" ? "bg-violet-500 text-white" : "text-slate-600 dark:text-slate-300"}`}
                  data-testid="preview-mode-desktop"
                >
                  <Monitor className="w-3.5 h-3.5" />Desktop
                </button>
                <button
                  onClick={() => setPreviewMode("mobile")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${previewMode === "mobile" ? "bg-violet-500 text-white" : "text-slate-600 dark:text-slate-300"}`}
                  data-testid="preview-mode-mobile"
                >
                  <Smartphone className="w-3.5 h-3.5" />Mobile
                </button>
              </div>
              <button
                onClick={() => setPreviewKey(k => k + 1)}
                className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
                title={language === "it" ? "Ricarica anteprima" : "Reload preview"}
                data-testid="reload-preview"
              >
                <RefreshCw className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="bg-slate-900 rounded-2xl p-3 overflow-hidden">
              <div className={`mx-auto rounded-xl overflow-hidden bg-white shadow-2xl transition-all ${previewMode === "mobile" ? "max-w-[390px]" : "w-full"}`}>
                <iframe
                  key={previewKey}
                  src={`${previewUrl}?_v=${previewKey}`}
                  title="preview"
                  className="w-full"
                  style={{ height: previewMode === "mobile" ? "780px" : "780px", border: 0 }}
                  data-testid="preview-iframe"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 text-center mt-2">
              {language === "it" ? "Anteprima live · si aggiorna ad ogni salvataggio" : "Live preview · updates on every save"}
            </p>
          </div>
        </div>

        {/* Mobile preview button */}
        <div className="lg:hidden fixed bottom-4 right-4 z-30">
          <Button
            onClick={() => window.open(previewUrl, "_blank")}
            className="rounded-full shadow-xl"
            style={{ background: `linear-gradient(135deg, ${branding.primary_color || "#6366f1"}, ${branding.secondary_color || "#8b5cf6"})` }}
            data-testid="mobile-open-preview"
          >
            <Eye className="w-4 h-4 mr-1" />{language === "it" ? "Anteprima" : "Preview"}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SmartPageEditor;
